import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { Json } from "@/integrations/supabase/types";

export interface EmailTemplateDesign {
  primaryColor: string;
  textColor: string;
  backgroundColor: string;
  buttonColor: string;
  buttonTextColor: string;
  headerLogoUrl?: string;
  footerText: string;
}

export interface EmailTemplate {
  id: string;
  key: string;
  name: string;
  description: string | null;
  subject_template: string;
  html_template: string;
  text_template: string | null;
  design_json: EmailTemplateDesign;
  is_active: boolean;
  updated_at: string;
  created_at: string;
}

export interface EmailTemplateVersion {
  id: string;
  template_id: string;
  version_number: number;
  subject_template: string;
  html_template: string;
  text_template: string | null;
  design_json: EmailTemplateDesign;
  created_at: string;
  created_by: string;
}

// CTA action to URL mapping - these are locked and cannot be edited by admins
export const CTA_ACTION_URLS: Record<string, string> = {
  dashboard: "/dashboard",
  directory: "/directory",
  connections: "/connections",
  profile: "/profile",
  view_connection: "/connections",
  view_message: "/connections",
  view_help_request: "/courses",
  view_discussion: "/discussions",
  schedule_call: "/schedule",
  admin_view_profile: "/admin",
  announcement_cta: "", // Dynamic, set at send time
};

// Sample data for preview - NO real PII
export const SAMPLE_PREVIEW_DATA: Record<string, string> = {
  first_name: "Alex",
  full_name: "Alex Johnson",
  mentor_name: "Dr. Sarah Williams",
  entrepreneur_name: "Alex Johnson",
  user_name: "Alex Johnson",
  recipient_name: "Alex Johnson",
  sender_name: "Jordan Smith",
  replier_name: "Jordan Smith",
  author_name: "Alex Johnson",
  requester_name: "Alex Johnson",
  other_party_name: "Dr. Sarah Williams",
  app_name: "EAB Connect",
  support_email: "support@eabconnect.com",
  year: new Date().getFullYear().toString(),
  title: "Sample Announcement Title",
  body: "<p>This is sample announcement content for preview purposes.</p>",
  message: "I would love to connect with you to discuss my startup journey.",
  message_preview: "This is a preview of the message content...",
  public_reason: "The mentor is currently at capacity with mentees.",
  cancel_reason: "Schedule conflict",
  scheduled_time: "January 15, 2025 at 2:00 PM EST",
  course_name: "Business Fundamentals",
  content: "I'm having trouble understanding the financial projections section.",
  discussion_title: "Marketing Strategies for Startups",
  reply_preview: "Great point! I would also add that...",
  role: "Entrepreneur",
  registered_at: new Date().toLocaleDateString(),
  cta_label: "Learn More",
};

export function useEmailTemplates() {
  return useQuery({
    queryKey: ["email-templates"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("email_templates")
        .select("*")
        .order("name");

      if (error) throw error;
      return (data || []).map(item => ({
        ...item,
        design_json: item.design_json as unknown as EmailTemplateDesign
      })) as EmailTemplate[];
    },
  });
}

export function useEmailTemplate(id: string | null) {
  return useQuery({
    queryKey: ["email-template", id],
    queryFn: async () => {
      if (!id) return null;
      const { data, error } = await supabase
        .from("email_templates")
        .select("*")
        .eq("id", id)
        .single();

      if (error) throw error;
      return {
        ...data,
        design_json: data.design_json as unknown as EmailTemplateDesign
      } as EmailTemplate;
    },
    enabled: !!id,
  });
}

export function useEmailTemplateVersions(templateId: string | null) {
  return useQuery({
    queryKey: ["email-template-versions", templateId],
    queryFn: async () => {
      if (!templateId) return [];
      const { data, error } = await supabase
        .from("email_template_versions")
        .select("*")
        .eq("template_id", templateId)
        .order("version_number", { ascending: false });

      if (error) throw error;
      return (data || []).map(item => ({
        ...item,
        design_json: item.design_json as unknown as EmailTemplateDesign
      })) as EmailTemplateVersion[];
    },
    enabled: !!templateId,
  });
}

export function useUpdateEmailTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      updates,
      createVersion = true,
    }: {
      id: string;
      updates: Partial<Omit<EmailTemplate, 'design_json'>> & { design_json?: EmailTemplateDesign };
      createVersion?: boolean;
    }) => {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Get current template for version creation
      if (createVersion) {
        const { data: currentTemplate, error: fetchError } = await supabase
          .from("email_templates")
          .select("*")
          .eq("id", id)
          .single();

        if (fetchError) throw fetchError;

        // Get latest version number
        const { data: versions } = await supabase
          .from("email_template_versions")
          .select("version_number")
          .eq("template_id", id)
          .order("version_number", { ascending: false })
          .limit(1);

        const nextVersion = versions && versions.length > 0 
          ? versions[0].version_number + 1 
          : 1;

        // Create version record
        const { error: versionError } = await supabase
          .from("email_template_versions")
          .insert({
            template_id: id,
            version_number: nextVersion,
            subject_template: currentTemplate.subject_template,
            html_template: currentTemplate.html_template,
            text_template: currentTemplate.text_template,
            design_json: currentTemplate.design_json,
            created_by: user.id,
          });

        if (versionError) throw versionError;
      }

      // Update template
      const { data, error } = await supabase
        .from("email_templates")
        .update(updates as Record<string, unknown>)
        .eq("id", id)
        .select()
        .single();

      if (error) throw error;
      return {
        ...data,
        design_json: data.design_json as unknown as EmailTemplateDesign
      } as EmailTemplate;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["email-templates"] });
      queryClient.invalidateQueries({ queryKey: ["email-template"] });
      queryClient.invalidateQueries({ queryKey: ["email-template-versions"] });
      toast.success("Template saved successfully");
    },
    onError: (error) => {
      toast.error("Failed to save template: " + error.message);
    },
  });
}

export function useRevertToVersion() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      templateId,
      version,
    }: {
      templateId: string;
      version: EmailTemplateVersion;
    }) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Create a new version with current state before reverting
      const { data: currentTemplate, error: fetchError } = await supabase
        .from("email_templates")
        .select("*")
        .eq("id", templateId)
        .single();

      if (fetchError) throw fetchError;

      // Get latest version number
      const { data: versions } = await supabase
        .from("email_template_versions")
        .select("version_number")
        .eq("template_id", templateId)
        .order("version_number", { ascending: false })
        .limit(1);

      const nextVersion = versions && versions.length > 0 
        ? versions[0].version_number + 1 
        : 1;

      // Save current state as a version
      await supabase
        .from("email_template_versions")
        .insert({
          template_id: templateId,
          version_number: nextVersion,
          subject_template: currentTemplate.subject_template,
          html_template: currentTemplate.html_template,
          text_template: currentTemplate.text_template,
          design_json: currentTemplate.design_json,
          created_by: user.id,
        });

      // Revert to selected version
      const { data, error } = await supabase
        .from("email_templates")
        .update({
          subject_template: version.subject_template,
          html_template: version.html_template,
          text_template: version.text_template,
          design_json: version.design_json as unknown as Json,
        })
        .eq("id", templateId)
        .select()
        .single();

      if (error) throw error;
      return {
        ...data,
        design_json: data.design_json as unknown as EmailTemplateDesign
      } as EmailTemplate;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["email-templates"] });
      queryClient.invalidateQueries({ queryKey: ["email-template"] });
      queryClient.invalidateQueries({ queryKey: ["email-template-versions"] });
      toast.success("Template reverted successfully");
    },
    onError: (error) => {
      toast.error("Failed to revert template: " + error.message);
    },
  });
}

// HTML sanitization - allowlist approach
const ALLOWED_TAGS = [
  "p", "br", "b", "strong", "i", "em", "u", "ul", "ol", "li",
  "h1", "h2", "h3", "h4", "blockquote", "a", "img", "table",
  "thead", "tbody", "tr", "th", "td", "div", "span"
];

const ALLOWED_ATTRIBUTES: Record<string, string[]> = {
  a: ["href", "target"],
  img: ["src", "alt", "width", "height"],
  "*": ["style", "class"],
};

export function sanitizeHtml(html: string): string {
  // Create a temporary element to parse HTML
  const doc = new DOMParser().parseFromString(html, "text/html");
  
  // Remove script tags and event handlers
  const scripts = doc.querySelectorAll("script, style, iframe, object, embed");
  scripts.forEach(el => el.remove());
  
  // Remove event handlers from all elements
  const allElements = doc.querySelectorAll("*");
  allElements.forEach(el => {
    // Remove on* attributes
    Array.from(el.attributes).forEach(attr => {
      if (attr.name.startsWith("on") || attr.name === "javascript") {
        el.removeAttribute(attr.name);
      }
    });
    
    // Check if tag is allowed
    if (!ALLOWED_TAGS.includes(el.tagName.toLowerCase())) {
      // Keep text content but remove the tag
      const text = document.createTextNode(el.textContent || "");
      el.parentNode?.replaceChild(text, el);
    }
  });
  
  return doc.body.innerHTML;
}

// Render template with variables and locked CTAs
export function renderTemplate(
  htmlTemplate: string,
  variables: Record<string, string>,
  design: EmailTemplateDesign,
  baseUrl: string = "https://announcements.eabconnect.com"
): string {
  let rendered = htmlTemplate;
  
  // Replace variables
  Object.entries(variables).forEach(([key, value]) => {
    const regex = new RegExp(`\\{\\{${key}\\}\\}`, "g");
    rendered = rendered.replace(regex, value);
  });
  
  // Replace button components with locked URLs
  const buttonRegex = /\{\{BUTTON_PRIMARY\s+label="([^"]+)"\s+action="([^"]+)"\}\}/g;
  rendered = rendered.replace(buttonRegex, (_, label, action) => {
    const url = CTA_ACTION_URLS[action] || "#";
    const fullUrl = url.startsWith("http") ? url : `${baseUrl}${url}`;
    return `<a href="${fullUrl}" style="display: inline-block; padding: 12px 24px; background-color: ${design.buttonColor}; color: ${design.buttonTextColor}; text-decoration: none; border-radius: 6px; font-weight: 600;">${label}</a>`;
  });
  
  return rendered;
}

// Build full email HTML with design
export function buildFullEmailHtml(
  bodyHtml: string,
  design: EmailTemplateDesign,
  logoUrl?: string
): string {
  const logo = logoUrl || design.headerLogoUrl || "";
  const footerText = design.footerText.replace("{{year}}", new Date().getFullYear().toString());
  
  return `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="margin: 0; padding: 0; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; background-color: ${design.backgroundColor};">
  <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background-color: ${design.backgroundColor};">
    <tr>
      <td align="center" style="padding: 40px 20px;">
        <table role="presentation" width="600" cellspacing="0" cellpadding="0" style="background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
          <!-- Header -->
          <tr>
            <td style="background-color: ${design.primaryColor}; padding: 24px; text-align: center;">
              ${logo ? `<img src="${logo}" alt="EAB Connect" style="max-height: 50px; max-width: 200px;">` : `<h1 style="margin: 0; color: #ffffff; font-size: 24px;">EAB Connect</h1>`}
            </td>
          </tr>
          <!-- Body -->
          <tr>
            <td style="padding: 32px; color: ${design.textColor}; font-size: 16px; line-height: 1.6;">
              ${bodyHtml}
            </td>
          </tr>
          <!-- Footer -->
          <tr>
            <td style="background-color: #f8f9fa; padding: 24px; text-align: center; color: #6c757d; font-size: 12px;">
              ${footerText}
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;
}
